import { useState, useMemo, useRef, useCallback, useEffect } from "react";
import { ArrowLeft, ChevronLeft, ChevronRight, ChevronUp, ChevronDown, FileText } from "lucide-react";
import MarkdownRenderer from "../MarkdownRenderer";

/** Two-column icon: synced = lines aligned, unsynced = lines offset */
function SyncScrollIcon({ synced, className }: { synced: boolean; className?: string }) {
  return (
    <svg viewBox="0 0 24 18" fill="none" className={className} stroke="currentColor" strokeLinecap="round">
      {/* left pane */}
      <rect x="1" y="1" width="9.5" height="16" rx="1.5" strokeWidth="1.4" />
      <line x1="3.5" y1="5" x2="8" y2="5" strokeWidth="1.6" opacity="0.7" />
      <line x1="3.5" y1="9" x2="8" y2="9" strokeWidth="1.6" opacity="0.7" />
      <line x1="3.5" y1="13" x2="6.5" y2="13" strokeWidth="1.6" opacity="0.7" />
      {/* right pane */}
      <rect x="13.5" y="1" width="9.5" height="16" rx="1.5" strokeWidth="1.4" />
      {synced ? (<>
        <line x1="16" y1="5" x2="20.5" y2="5" strokeWidth="1.6" opacity="0.7" />
        <line x1="16" y1="9" x2="20.5" y2="9" strokeWidth="1.6" opacity="0.7" />
        <line x1="16" y1="13" x2="19" y2="13" strokeWidth="1.6" opacity="0.7" />
      </>) : (<>
        <line x1="16" y1="4" x2="20.5" y2="4" strokeWidth="1.6" opacity="0.7" />
        <line x1="16" y1="8" x2="20.5" y2="8" strokeWidth="1.6" opacity="0.7" />
        <line x1="16" y1="12" x2="19" y2="12" strokeWidth="1.6" opacity="0.7" />
      </>)}
    </svg>
  );
}
import { computeMarkdownDiff, buildRenderSegments, type DiffSegment } from "../../utils/markdownDiff";
import * as yaml from "js-yaml";
import { useSyncScroll } from "../../hooks/useSyncScroll";
import {
  RouteSection, AuthSection, SummarySection,
  ParametersSection, RequestBodySection, ResponsesSection,
} from "./ApiSpecDiffView";
import CardDiffView from "./CardDiffView";
import SupportMetaDiffView, { type SupportMeta } from "./SupportMetaDiffView";

const BADGE: Record<string, { label: string; bg: string }> = {
  added:     { label: "ADD", bg: "bg-emerald-100 text-emerald-600" },
  deleted:   { label: "DEL", bg: "bg-red-100 text-red-500" },
  modified:  { label: "MOD", bg: "bg-blue-100 text-blue-600" },
  moved:     { label: "MOV", bg: "bg-violet-100 text-violet-600" },
  renamed:   { label: "REN", bg: "bg-fuchsia-100 text-fuchsia-600" },
  reordered: { label: "ORD", bg: "bg-amber-100 text-amber-600" },
};

const CHANGE_TYPE_BADGE: Record<string, { label: string; bg: string }> = {
  content:         { label: "Content", bg: "bg-blue-50 text-blue-500" },
  title:           { label: "Title", bg: "bg-sky-50 text-sky-600" },
  spec:            { label: "Spec", bg: "bg-orange-50 text-orange-500" },
  reorder:         { label: "Reorder", bg: "bg-amber-50 text-amber-500" },
  visibility:      { label: "Visibility", bg: "bg-purple-50 text-purple-500" },
  metadata:        { label: "Metadata", bg: "bg-slate-100 text-slate-500" },
  type:            { label: "Type", bg: "bg-pink-50 text-pink-500" },
  slug:            { label: "Slug", bg: "bg-teal-50 text-teal-600" },
  followed_parent: { label: "Cascade", bg: "bg-slate-100 text-slate-400" },
};

type DiffViewMode = "rendered" | "source";

function prettyJson(body: string): string {
  if (!body.trim()) return "";
  try {
    const parsed = JSON.parse(body);
    return yaml.dump(parsed, { indent: 2, lineWidth: -1, noRefs: true });
  } catch {
    return body;
  }
}

export interface RenderedDiffViewProps {
  beforeBody: string;
  afterBody: string;
  title?: string;
  slugPath?: string;
  changeActions?: string[];
  changeTypes?: string[];
  loading?: boolean;
  onBack?: () => void;
  onPrev?: () => void;
  onNext?: () => void;
  currentIndex?: number;
  totalCount?: number;
  beforeLabel?: string;
  afterLabel?: string;
  maxHeight?: string;
  showHeader?: boolean;
  changedAssets?: { filename: string; action: string }[];
  nodeType?: string;
  beforeTitle?: string;
  afterTitle?: string;
  externalMode?: DiffViewMode;
  onModeChange?: (m: DiffViewMode) => void;
  externalSyncScroll?: boolean;
  onSyncToggle?: () => void;
  beforeSpec?: Record<string, any> | null;
  afterSpec?: Record<string, any> | null;
  /** Support / FAQ typed metadata blob — wires SupportMetaDiffView. */
  beforeMeta?: Record<string, any> | null;
  afterMeta?: Record<string, any> | null;
  /** Section type of the containing section (used to dispatch the
   *  typed-metadata renderer). */
  sectionType?: string | null;
}

const diffStyles = `
.diff-segment-add { background: rgba(34,197,94,0.12); border-left: 3px solid rgb(34,197,94); padding: 4px 10px; margin: 3px 0; border-radius: 4px; }
.diff-segment-del { background: rgba(239,68,68,0.12); border-left: 3px solid rgb(239,68,68); padding: 4px 10px; margin: 3px 0; border-radius: 4px; }
.diff-segment-mod { background: rgba(234,179,8,0.08); border-left: 3px solid rgb(234,179,8); padding: 4px 10px; margin: 3px 0; border-radius: 4px; }
.diff-word-add { background: rgba(34,197,94,0.25); border-radius: 3px; padding: 1px 3px; }
.diff-word-del { background: rgba(239,68,68,0.25); border-radius: 3px; padding: 1px 3px; }
td .diff-word-add { background: rgba(34,197,94,0.25); border-radius: 3px; padding: 1px 3px; }
td .diff-word-del { background: rgba(239,68,68,0.25); border-radius: 3px; padding: 1px 3px; }
[data-diff-hl="add"] { background: rgba(34,197,94,0.10); border-left: 3px solid rgb(34,197,94); padding-left: 10px; margin: 3px 0; border-radius: 4px; }
[data-diff-hl="del"] { background: rgba(239,68,68,0.10); border-left: 3px solid rgb(239,68,68); padding-left: 10px; margin: 3px 0; border-radius: 4px; }
[data-diff-hl="mod"] { background: rgba(234,179,8,0.08); border-left: 3px solid rgb(234,179,8); padding-left: 10px; margin: 3px 0; border-radius: 4px; }
`;

/**
 * Build the full markdown for one side of the diff, with `<div data-diff-hl="...">` 
 * wrappers around changed blocks.
 *
 * The markdown inside the wrappers is identical to the original — the div just
 * adds a visual highlight overlay via CSS. Since MarkdownRenderer uses rehype-raw,
 * block-level HTML elements pass through untouched.
 *
 * Block-level HTML in markdown requires blank lines before and after to be
 * recognized as block elements (not inline). We ensure this by always adding
 * blank line separators around the wrapper divs.
 */
function buildHighlightedMarkdown(
  segments: DiffSegment[],
  side: "before" | "after",
): string {
  const renderSegs = buildRenderSegments(segments, side);
  const parts: string[] = [];

  for (const seg of renderSegs) {
    if (seg.type === "context") {
      parts.push(seg.markdown);
    } else {
      const hlType = seg.type === "removed" ? "del" : seg.type === "added" ? "add" : "mod";
      // Wrap the entire block in a highlight div.
      // Block-level HTML in markdown needs blank lines around it.
      parts.push("");
      parts.push(`<div data-diff-hl="${hlType}">`);
      parts.push("");
      parts.push(seg.markdown);
      parts.push("");
      parts.push("</div>");
      parts.push("");
    }
  }

  return parts.join("\n");
}

export default function RenderedDiffView({
  beforeBody, afterBody, title, slugPath, changeActions = [], changeTypes = [], loading = false,
  onBack, onPrev, onNext, currentIndex, totalCount,
  beforeLabel = "Before", afterLabel = "After", maxHeight = "100%",
  showHeader, changedAssets, nodeType, beforeTitle, afterTitle,
  externalMode, onModeChange, externalSyncScroll, onSyncToggle,
  beforeSpec, afterSpec,
  beforeMeta, afterMeta, sectionType,
}: RenderedDiffViewProps) {
  const [internalMode, setInternalMode] = useState<DiffViewMode>("rendered");
  const [internalSync, setInternalSync] = useState(true);

  // Convert card content to markdown for rendered diff
  const effectiveBeforeBody = beforeBody;
  const effectiveAfterBody = afterBody;
  const mode = externalMode ?? internalMode;
  const setMode = onModeChange ?? setInternalMode;
  const syncScroll = externalSyncScroll ?? internalSync;
  const toggleSync = onSyncToggle ?? (() => setInternalSync(s => !s));
  const { beforeRef, afterRef, pauseSync } = useSyncScroll(syncScroll);

  const shouldShowHeader = showHeader ?? !!onBack;
  const normForCompare = (s: string) => s.replace(/\r\n/g, "\n").split("\n").map(l => l.trimEnd()).join("\n");
  const noChanges = normForCompare(effectiveBeforeBody) === normForCompare(effectiveAfterBody);
  const isAddOrDelete = changeActions.includes("added") || changeActions.includes("deleted");
  const effectiveChangedAssets = isAddOrDelete ? undefined : changedAssets;
  const hasAssetChanges = effectiveChangedAssets && effectiveChangedAssets.length > 0;
  const hasSpec = !!(beforeSpec || afterSpec);
  // Support / FAQ pages also have a typed metadata blob that may have
  // changed even when the body is identical. The renderer mounts
  // SupportMetaDiffView above the body diff for these.
  const isSupportLike = sectionType === "support" || sectionType === "faq";
  const hasMetaDiff = isSupportLike && !!(beforeMeta || afterMeta);

  // For card nodes in source mode, pretty-print JSON for readable diff
  const sourceBeforeBody = nodeType === "card" ? prettyJson(beforeBody) : effectiveBeforeBody;
  const sourceAfterBody = nodeType === "card" ? prettyJson(afterBody) : effectiveAfterBody;

  const segments = useMemo(
    () => (noChanges && nodeType !== "card" ? [] : computeMarkdownDiff(sourceBeforeBody, sourceAfterBody)),
    [sourceBeforeBody, sourceAfterBody, noChanges, nodeType],
  );

  // Build highlighted markdown for each side (full markdown with <div> wrappers on changed blocks)
  const beforeHighlighted = useMemo(
    () => (noChanges ? effectiveBeforeBody : buildHighlightedMarkdown(segments, "before")),
    [segments, noChanges, effectiveBeforeBody],
  );
  const afterHighlighted = useMemo(
    () => (noChanges ? effectiveAfterBody : buildHighlightedMarkdown(segments, "after")),
    [segments, noChanges, effectiveAfterBody],
  );

  return (
    <div className="flex-1 flex flex-col min-h-0" style={{ maxHeight }}>
      <style>{diffStyles}</style>

      {shouldShowHeader && (
        <DiffHeader title={title} slugPath={slugPath} changeActions={changeActions} changeTypes={changeTypes}
          mode={mode} onModeChange={setMode} syncScroll={syncScroll}
          onSyncToggle={toggleSync} onBack={onBack}
          onPrev={onPrev} onNext={onNext} currentIndex={currentIndex} totalCount={totalCount} />
      )}

      {loading ? (
        <div className="flex-1 flex items-center justify-center text-slate-400 text-[13px]">Loading…</div>
      ) : noChanges && !hasAssetChanges && !hasSpec && !hasMetaDiff && nodeType !== "card" ? (
        <div className="flex-1 flex items-center justify-center text-slate-400 text-[13px] italic">No content changes</div>
      ) : (
        <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
          {hasMetaDiff && (
            <div className="px-4 pt-3 shrink-0">
              <SupportMetaDiffView
                before={beforeMeta as SupportMeta | null | undefined}
                after={afterMeta as SupportMeta | null | undefined}
                beforeLabel={beforeLabel}
                afterLabel={afterLabel}
              />
            </div>
          )}
          {nodeType === "card" && mode !== "source" ? (
            <CardDiffView
              key="card-diff"
              beforeBody={beforeBody} afterBody={afterBody}
              beforeLabel={beforeLabel} afterLabel={afterLabel}
              slugPath={slugPath} />
          ) : mode === "source" && !noChanges ? (
            <SourcePanels key="source" segments={segments} beforeLabel={beforeLabel} afterLabel={afterLabel} beforeRef={beforeRef} afterRef={afterRef} />
          ) : hasAssetChanges ? (
            <InlineAssetDiffPanels
              key="inline-assets"
              beforeBody={effectiveBeforeBody} afterBody={effectiveAfterBody}
              beforeLabel={beforeLabel} afterLabel={afterLabel}
              changedAssets={effectiveChangedAssets!} slugPath={slugPath}
              noTextChanges={noChanges}
              beforeHighlighted={beforeHighlighted} afterHighlighted={afterHighlighted}
              changeActions={changeActions}
              beforeTitle={beforeTitle} afterTitle={afterTitle} />
          ) : (
            <RenderedPanels
              key="rendered"
              beforeHighlighted={beforeHighlighted} afterHighlighted={afterHighlighted}
              beforeLabel={beforeLabel} afterLabel={afterLabel}
              beforeBody={effectiveBeforeBody} afterBody={effectiveAfterBody}
              changeActions={changeActions} beforeRef={beforeRef} afterRef={afterRef}
              beforeTitle={beforeTitle} afterTitle={afterTitle}
              pauseSync={pauseSync}
              beforeSpec={beforeSpec} afterSpec={afterSpec} />
          )}
        </div>
      )}
    </div>
  );
}


function DiffHeader({
  title, slugPath, changeActions, changeTypes = [], mode, onModeChange, syncScroll, onSyncToggle, onBack,
  onPrev, onNext, currentIndex, totalCount, hideModeSwitcher,
}: {
  title?: string; slugPath?: string; changeActions: string[]; changeTypes?: string[];
  mode: DiffViewMode; onModeChange: (m: DiffViewMode) => void;
  syncScroll: boolean; onSyncToggle: () => void; onBack?: () => void;
  onPrev?: () => void; onNext?: () => void; currentIndex?: number; totalCount?: number;
  hideModeSwitcher?: boolean;
}) {
  const hasNav = (totalCount ?? 0) > 0;
  return (
    <div className="flex items-center gap-3 px-4 py-2.5 border-b border-slate-200 bg-slate-50/80 shrink-0">
      {onBack && (
        <button onClick={onBack} className="shrink-0 flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-[13px] font-medium text-slate-700 bg-white border border-slate-200 hover:border-indigo-400 hover:text-indigo-600 shadow-sm transition-colors">
          <ArrowLeft className="size-4" /> Back
        </button>
      )}
      <div className="flex items-center gap-2 min-w-0 flex-1">
        {changeActions.map(a => {
          const cfg = BADGE[a];
          if (!cfg) return null;
          return <span key={a} className={`shrink-0 text-[11px] px-2 py-0.5 rounded font-semibold ${cfg.bg}`}>{cfg.label}</span>;
        })}
        {changeTypes && changeTypes.length > 0 && (
          <span className="flex items-center gap-0.5 shrink-0">
            {changeTypes.filter(t => t !== "followed_parent").map(t => {
              const cfg = CHANGE_TYPE_BADGE[t];
              if (!cfg) return null;
              return <span key={t} className={`text-[9px] px-1.5 py-px rounded font-medium ${cfg.bg}`}>{cfg.label}</span>;
            })}
          </span>
        )}
        <FileText className="size-4 text-slate-400 shrink-0" />
        {title && <span className="text-[14px] font-semibold text-slate-800 truncate">{title}</span>}
        {slugPath && <span className="text-[12px] font-mono text-slate-400 truncate">{slugPath}</span>}
      </div>
      {hasNav && (
        <div className="flex items-center rounded-md border border-slate-200 bg-white shadow-sm overflow-hidden shrink-0">
          <button onClick={onPrev} disabled={!onPrev}
            className="px-2 py-1.5 text-slate-500 hover:text-indigo-600 hover:bg-indigo-50 disabled:text-slate-200 disabled:cursor-not-allowed transition-colors border-r border-slate-200" title="Previous change">
            <ChevronLeft className="size-4" />
          </button>
          <span className="px-3 py-1.5 text-[12px] text-slate-600 font-semibold tabular-nums select-none bg-slate-50/50">
            {currentIndex != null && totalCount != null ? `${currentIndex + 1} / ${totalCount}` : "–"}
          </span>
          <button onClick={onNext} disabled={!onNext}
            className="px-2 py-1.5 text-slate-500 hover:text-indigo-600 hover:bg-indigo-50 disabled:text-slate-200 disabled:cursor-not-allowed transition-colors border-l border-slate-200" title="Next change">
            <ChevronRight className="size-4" />
          </button>
        </div>
      )}
      {!hideModeSwitcher && (
        <div className="flex rounded-md bg-slate-100 p-0.5 text-[12px] shrink-0">
          <button onClick={() => onModeChange("rendered")}
            className={`px-3 py-1.5 font-medium rounded-md transition-colors ${mode === "rendered" ? "bg-indigo-50 text-indigo-700 border border-indigo-200 shadow-sm" : "text-slate-500 hover:text-indigo-600 hover:bg-indigo-50/50"}`}>
            Rendered
          </button>
          <button onClick={() => onModeChange("source")}
            className={`px-3 py-1.5 font-medium rounded-md transition-colors ${mode === "source" ? "bg-indigo-50 text-indigo-700 border border-indigo-200 shadow-sm" : "text-slate-500 hover:text-indigo-600 hover:bg-indigo-50/50"}`}>
            Source
          </button>
        </div>
      )}
      <button onClick={onSyncToggle} title={syncScroll ? "Unlock pane scrolling" : "Lock pane scrolling"}
        className={`p-1.5 rounded-md border shadow-sm transition-colors shrink-0 ${syncScroll ? "text-indigo-600 bg-indigo-50 border-indigo-200" : "text-slate-400 bg-white border-slate-200 hover:border-slate-300"}`}>
        <SyncScrollIcon synced={syncScroll} className="w-[22px] h-[16px]" />
      </button>
    </div>
  );
}


/**
 * RenderedPanels — renders FULL markdown on each side with highlight wrappers.
 * The rendering is identical to the live page; changed blocks are wrapped in
 * <div data-diff-hl="..."> which adds a subtle colored overlay via CSS.
 * Includes ▲/▼ navigation to jump between changed blocks.
 */
function RenderedPanels({
  beforeHighlighted, afterHighlighted, beforeLabel, afterLabel,
  beforeBody, afterBody, changeActions, beforeRef, afterRef,
  beforeTitle, afterTitle, pauseSync,
  beforeSpec, afterSpec,
}: {
  beforeHighlighted: string; afterHighlighted: string;
  beforeLabel: string; afterLabel: string;
  beforeBody: string; afterBody: string; changeActions: string[];
  beforeRef: React.Ref<HTMLDivElement>;
  afterRef: React.Ref<HTMLDivElement>;
  beforeTitle?: string; afterTitle?: string;
  pauseSync: (ms?: number) => () => void;
  beforeSpec?: Record<string, any> | null;
  afterSpec?: Record<string, any> | null;
}) {
  const isDeleted = changeActions.includes("deleted");
  const isAdded = changeActions.includes("added");
  const titleChanged = beforeTitle && afterTitle && beforeTitle !== afterTitle;

  // Refs for both scroll panels
  const beforePanelRef = useRef<HTMLDivElement>(null);
  const afterPanelRef = useRef<HTMLDivElement>(null);
  const [focusIdx, setFocusIdx] = useState(-1);
  const [changeTotal, setChangeTotal] = useState(0);

  // Collect change entries from both panels without double-counting.
  // - "del" highlights only exist on the before (left) panel
  // - "add" and "mod" highlights only exist on the after (right) panel
  // For modifications, both panels have a highlight but we only count the after side.
  type ChangeEntry = { el: HTMLElement; panel: HTMLDivElement };
  const getChangeEntries = useCallback((): ChangeEntry[] => {
    const entries: ChangeEntry[] = [];
    const bPanel = beforePanelRef.current;
    const aPanel = afterPanelRef.current;
    // From before panel: only deletions (they don't appear on the after side)
    if (bPanel) {
      for (const el of Array.from(bPanel.querySelectorAll('[data-diff-hl="del"]')) as HTMLElement[]) {
        entries.push({ el, panel: bPanel });
      }
    }
    // From after panel: additions and modifications
    if (aPanel) {
      for (const el of Array.from(aPanel.querySelectorAll('[data-diff-hl="add"], [data-diff-hl="mod"]')) as HTMLElement[]) {
        entries.push({ el, panel: aPanel });
      }
    }
    // Sort by vertical position
    entries.sort((a, b) => a.el.getBoundingClientRect().top - b.el.getBoundingClientRect().top);
    return entries;
  }, []);

  const scrollToEntry = useCallback((entry: ChangeEntry) => {
    const { el, panel } = entry;
    const resume = pauseSync(800);
    const panelRect = panel.getBoundingClientRect();
    const elRect = el.getBoundingClientRect();
    const offset = elRect.top - panelRect.top + panel.scrollTop - panelRect.height / 3;
    panel.scrollTo({ top: Math.max(0, offset), behavior: "smooth" });

    // For modifications, also scroll the opposite panel to its corresponding highlight
    const hlType = el.getAttribute("data-diff-hl");
    if (hlType === "mod") {
      const otherPanel = panel === afterPanelRef.current ? beforePanelRef.current : afterPanelRef.current;
      if (otherPanel) {
        // Find the matching mod element on the other side by index
        const sameSideEls = Array.from(panel.querySelectorAll('[data-diff-hl="mod"]'));
        const idx = sameSideEls.indexOf(el);
        const otherEls = Array.from(otherPanel.querySelectorAll('[data-diff-hl="mod"]'));
        const otherEl = otherEls[idx];
        if (otherEl) {
          const oRect = otherPanel.getBoundingClientRect();
          const oeRect = otherEl.getBoundingClientRect();
          const oOffset = oeRect.top - oRect.top + otherPanel.scrollTop - oRect.height / 3;
          otherPanel.scrollTo({ top: Math.max(0, oOffset), behavior: "smooth" });
        }
      }
    }

    setTimeout(resume, 600);
  }, [pauseSync]);

  // Count changes once content renders
  useEffect(() => {
    let cancelled = false;
    const check = () => {
      if (cancelled) return;
      const entries = getChangeEntries();
      setChangeTotal(entries.length);
      if (entries.length > 0 && !cancelled) {
        setFocusIdx(0);
        scrollToEntry(entries[0]);
      }
    };
    const t1 = setTimeout(check, 50);
    const t2 = setTimeout(check, 300);
    const t3 = setTimeout(check, 800);
    return () => { cancelled = true; clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, [beforeHighlighted, afterHighlighted, getChangeEntries, scrollToEntry]);

  const jumpTo = useCallback((idx: number) => {
    const entries = getChangeEntries();
    if (entries.length === 0) return;
    const clamped = Math.max(0, Math.min(idx, entries.length - 1));
    setFocusIdx(clamped);
    scrollToEntry(entries[clamped]);
  }, [getChangeEntries, scrollToEntry]);

  const goPrev = useCallback(() => jumpTo(focusIdx - 1), [focusIdx, jumpTo]);
  const goNext = useCallback(() => jumpTo(focusIdx + 1), [focusIdx, jumpTo]);

  // Horizontal scroll sync between before/after panels
  const hSyncing = useRef(false);
  const beforeHScrollRef = useRef<HTMLDivElement>(null);
  const afterHScrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const left = beforeHScrollRef.current;
    const right = afterHScrollRef.current;
    if (!left || !right) return;

    function onLeftScroll() {
      if (hSyncing.current) return;
      hSyncing.current = true;
      afterHScrollRef.current!.scrollLeft = beforeHScrollRef.current!.scrollLeft;
      hSyncing.current = false;
    }
    function onRightScroll() {
      if (hSyncing.current) return;
      hSyncing.current = true;
      beforeHScrollRef.current!.scrollLeft = afterHScrollRef.current!.scrollLeft;
      hSyncing.current = false;
    }

    left.addEventListener("scroll", onLeftScroll);
    right.addEventListener("scroll", onRightScroll);
    return () => {
      left.removeEventListener("scroll", onLeftScroll);
      right.removeEventListener("scroll", onRightScroll);
    };
  }, []);

  return (
    <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
      {/* Shared header row: before label | after label + navigator */}
      <div className="flex shrink-0 border-b border-slate-200">
        <div className="flex-1 min-w-0 px-3 py-1.5 bg-indigo-50/60 border-r border-slate-200 flex items-center min-h-[32px]">
          <div className="size-1.5 rounded-full bg-indigo-400 mr-1.5" />
          <span className="text-[12px] font-semibold text-indigo-700">{beforeLabel}</span>
          {titleChanged && <span className="ml-2 text-[11px] text-slate-400">{beforeTitle}</span>}
        </div>
        <div className="flex-1 min-w-0 px-3 py-1.5 bg-orange-50/60 flex items-center justify-between min-h-[32px]">
          <span className="flex items-center text-[12px] font-semibold text-orange-700">
            <div className="size-1.5 rounded-full bg-orange-400 mr-1.5" />
            {afterLabel}
            {titleChanged && <span className="ml-2 text-[11px] text-slate-400 font-normal">{afterTitle}</span>}
          </span>
          {changeTotal > 0 && (
            <span className="flex items-center gap-0.5 ml-auto">
              <button onClick={goPrev} disabled={focusIdx <= 0}
                className="p-0.5 rounded text-slate-400 hover:text-indigo-600 disabled:text-slate-200 transition-colors" title="Previous change">
                <ChevronUp className="size-3.5" />
              </button>
              <span className="text-[11px] text-slate-400 tabular-nums min-w-[28px] text-center select-none">
                {focusIdx >= 0 ? focusIdx + 1 : "–"}/{changeTotal}
              </span>
              <button onClick={goNext} disabled={focusIdx >= changeTotal - 1}
                className="p-0.5 rounded text-slate-400 hover:text-indigo-600 disabled:text-slate-200 transition-colors" title="Next change">
                <ChevronDown className="size-3.5" />
              </button>
            </span>
          )}
        </div>
      </div>
      {/* Content panels — horizontal scrollable */}
      <div className="flex-1 flex min-h-0 overflow-hidden">
        <div className="flex-1 min-w-0 flex flex-col border-r border-slate-200 min-h-0">
          <div ref={(el) => {
            beforePanelRef.current = el;
            beforeHScrollRef.current = el;
            if (typeof beforeRef === "function") beforeRef(el);
            else if (beforeRef && "current" in beforeRef) (beforeRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
          }} className="flex-1 overflow-auto p-4 scrollbar-mac">
            <div className="min-w-[70vw]">
              {isAdded ? <span className="text-slate-300 italic text-[13px]">New file</span>
               : !beforeBody ? <span className="text-slate-300 italic text-[13px]">Empty</span>
               : isDeleted ? <div className="diff-segment-del"><MarkdownRenderer content={beforeBody} /></div>
               : <MarkdownRenderer content={beforeHighlighted} />}
              {beforeSpec && <InlineSpecSections spec={beforeSpec} otherSpec={afterSpec ?? null} side="before" />}
            </div>
          </div>
        </div>
        <div className="flex-1 min-w-0 flex flex-col min-h-0">
          <div ref={(el) => {
            afterPanelRef.current = el;
            afterHScrollRef.current = el;
            if (typeof afterRef === "function") afterRef(el);
            else if (afterRef && "current" in afterRef) (afterRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
          }} className="flex-1 overflow-auto p-4 scrollbar-mac">
            <div className="min-w-[70vw]">
              {isDeleted ? <span className="text-red-400 italic text-[13px]">Deleted</span>
               : !afterBody ? <span className="text-slate-300 italic text-[13px]">Empty</span>
               : isAdded ? <div className="diff-segment-add"><MarkdownRenderer content={afterBody} /></div>
               : <MarkdownRenderer content={afterHighlighted} />}
              {afterSpec && <InlineSpecSections spec={afterSpec} otherSpec={beforeSpec ?? null} side="after" />}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


/* ════════════════════════════════════════════════════════════════════
   InlineSpecSections — renders API spec fields as flat cards inline
   below the body markdown, inside the same scroll container.
   ════════════════════════════════════════════════════════════════════ */

const SPEC_SECTION_ORDER = ["route", "auth", "summary", "parameters", "requestBody", "responses"] as const;
type SpecSectionKey = typeof SPEC_SECTION_ORDER[number];
const SPEC_SECTION_LABELS: Record<SpecSectionKey, string> = {
  route: "Route", auth: "Auth", summary: "Summary",
  parameters: "Parameters", requestBody: "Request Body", responses: "Responses",
};

function specSectionPresent(key: SpecSectionKey, spec: Record<string, any>): boolean {
  switch (key) {
    case "route": return !!(spec.method || spec.path);
    case "auth": return !!spec.authType;
    case "summary": return !!spec.summary;
    case "parameters": return Array.isArray(spec.parameters) && spec.parameters.length > 0;
    case "requestBody": return !!spec.requestBody;
    case "responses": return !!spec.responses && Object.keys(spec.responses).length > 0;
  }
}

function specEq(a: unknown, b: unknown): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

function specSectionChanged(key: SpecSectionKey, a: Record<string, any>, b: Record<string, any>): boolean {
  switch (key) {
    case "route": return a.method !== b.method || a.path !== b.path;
    case "auth": return a.authType !== b.authType;
    case "summary": return a.summary !== b.summary;
    case "parameters": return !specEq(a.parameters, b.parameters);
    case "requestBody": return !specEq(a.requestBody, b.requestBody);
    case "responses": return !specEq(a.responses, b.responses);
  }
}

function InlineSpecSections({ spec, otherSpec, side }: {
  spec: Record<string, any>;
  otherSpec: Record<string, any> | null;
  side: "before" | "after";
}) {
  const visibleSections = SPEC_SECTION_ORDER.filter(key => {
    if (specSectionPresent(key, spec)) return true;
    if (otherSpec && specSectionPresent(key, otherSpec)) return true;
    return false;
  });

  if (visibleSections.length === 0) return null;

  return (
    <div className="mt-6 border-t border-slate-200 pt-4 space-y-2">
      <div className="flex items-center gap-2 mb-2">
        <span className="text-[11px] font-semibold text-violet-600 uppercase tracking-wider">API Spec</span>
        <div className="flex-1 h-px bg-slate-100" />
      </div>
      {visibleSections.map(key => {
        const present = specSectionPresent(key, spec);
        const otherPresent = otherSpec ? specSectionPresent(key, otherSpec) : false;
        const changed = otherSpec ? specSectionChanged(key, spec, otherSpec) : true;
        // Color the section header by what happened to it on this side:
        // - present here, missing on other → removed (before) / added (after)
        // - present on both, modified → blue
        // - present on both, unchanged → neutral
        let highlight = "border border-slate-100";
        if (present && !otherPresent) {
          highlight = side === "after"
            ? "border-l-2 border-l-emerald-300 bg-emerald-50/20"
            : "border-l-2 border-l-red-300 bg-red-50/20";
        } else if (present && otherPresent && changed) {
          highlight = "border-l-2 border-l-blue-300 bg-blue-50/20";
        } else if (!present && otherPresent) {
          highlight = "border border-slate-100 opacity-60";
        }
        return (
          <div key={key} className={`rounded-md ${highlight}`}>
            <div className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider px-2.5 py-1">
              {SPEC_SECTION_LABELS[key]}
            </div>
            <div className="px-2.5 pb-2">
              {!present ? (
                <span className="text-[12px] text-slate-300 italic">
                  ({side === "before" ? "added on right" : "removed on left"})
                </span>
              ) : (
                <SpecSectionContent sectionKey={key} spec={spec} otherSpec={otherSpec} side={side} />
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function SpecSectionContent({ sectionKey, spec, otherSpec, side }: {
  sectionKey: SpecSectionKey; spec: Record<string, any>; otherSpec: Record<string, any> | null;
  side: "before" | "after";
}) {
  switch (sectionKey) {
    case "route": return <RouteSection spec={spec} />;
    case "auth": return <AuthSection spec={spec} />;
    case "summary": return <SummarySection spec={spec} />;
    case "parameters":
      return <ParametersSection params={spec.parameters || []} diffParams={otherSpec?.parameters} side={side} />;
    case "requestBody":
      return <RequestBodySection schema={spec.requestBody} diffSchema={otherSpec?.requestBody} side={side} />;
    case "responses":
      return <ResponsesSection responses={spec.responses || {}} diffResponses={otherSpec?.responses} side={side} />;
  }
}


function SourcePanels({
  segments, beforeLabel, afterLabel, beforeRef, afterRef,
}: {
  segments: ReturnType<typeof computeMarkdownDiff>;
  beforeLabel: string; afterLabel: string;
  beforeRef: React.Ref<HTMLDivElement>;
  afterRef: React.Ref<HTMLDivElement>;
}) {
  const beforePanelRef = useRef<HTMLDivElement>(null);
  const afterPanelRef = useRef<HTMLDivElement>(null);

  // Horizontal scroll sync
  const hSyncing = useRef(false);
  useEffect(() => {
    const left = beforePanelRef.current;
    const right = afterPanelRef.current;
    if (!left || !right) return;
    function onLeftScroll() {
      if (hSyncing.current) return;
      hSyncing.current = true;
      afterPanelRef.current!.scrollLeft = beforePanelRef.current!.scrollLeft;
      hSyncing.current = false;
    }
    function onRightScroll() {
      if (hSyncing.current) return;
      hSyncing.current = true;
      beforePanelRef.current!.scrollLeft = afterPanelRef.current!.scrollLeft;
      hSyncing.current = false;
    }
    left.addEventListener("scroll", onLeftScroll);
    right.addEventListener("scroll", onRightScroll);
    return () => {
      left.removeEventListener("scroll", onLeftScroll);
      right.removeEventListener("scroll", onRightScroll);
    };
  }, []);

  // Build line arrays and track change-group boundaries (index of first line in each group)
  const { beforeLines, afterLines, changeGroups } = useMemo(() => {
    const bLines: { num: number; text: string; type: "context" | "removed" }[] = [];
    const aLines: { num: number; text: string; type: "context" | "added" }[] = [];
    // Each group: { beforeIdx: first line index in bLines, afterIdx: first line index in aLines }
    const groups: { beforeIdx: number; afterIdx: number; side: "before" | "after" | "both" }[] = [];
    let bNum = 0, aNum = 0;
    for (const seg of segments) {
      if (seg.type !== "context") {
        const grp = { beforeIdx: bLines.length, afterIdx: aLines.length, side: seg.type === "removed" ? "before" as const : "after" as const };
        // Merge with previous group if they're adjacent (no context between them)
        const prev = groups[groups.length - 1];
        if (prev && prev.beforeIdx + countType(bLines, prev.beforeIdx, "removed") === bLines.length
            && prev.afterIdx + countType(aLines, prev.afterIdx, "added") === aLines.length) {
          // Adjacent change — extend previous group
          if (prev.side !== grp.side) prev.side = "both";
        } else {
          groups.push(grp);
        }
      }
      for (const line of seg.lines) {
        if (seg.type === "context") { bNum++; aNum++; bLines.push({ num: bNum, text: line, type: "context" }); aLines.push({ num: aNum, text: line, type: "context" }); }
        else if (seg.type === "removed") { bNum++; bLines.push({ num: bNum, text: line, type: "removed" }); }
        else { aNum++; aLines.push({ num: aNum, text: line, type: "added" }); }
      }
    }
    return { beforeLines: bLines, afterLines: aLines, changeGroups: groups };
  }, [segments]);

  const [focusIdx, setFocusIdx] = useState(-1);
  const changeTotal = changeGroups.length;

  // Auto-focus first change
  useEffect(() => {
    if (changeGroups.length > 0) setFocusIdx(0);
  }, [changeGroups]);

  const scrollToGroup = useCallback((idx: number) => {
    if (idx < 0 || idx >= changeGroups.length) return;
    const grp = changeGroups[idx];
    // Scroll before panel
    const bPanel = beforePanelRef.current;
    if (bPanel && bPanel.children[grp.beforeIdx]) {
      const el = bPanel.children[grp.beforeIdx] as HTMLElement;
      const panelRect = bPanel.getBoundingClientRect();
      const elRect = el.getBoundingClientRect();
      bPanel.scrollTo({ top: elRect.top - panelRect.top + bPanel.scrollTop - panelRect.height / 3, behavior: "smooth" });
    }
    // Scroll after panel
    const aPanel = afterPanelRef.current;
    if (aPanel && aPanel.children[grp.afterIdx]) {
      const el = aPanel.children[grp.afterIdx] as HTMLElement;
      const panelRect = aPanel.getBoundingClientRect();
      const elRect = el.getBoundingClientRect();
      aPanel.scrollTo({ top: elRect.top - panelRect.top + aPanel.scrollTop - panelRect.height / 3, behavior: "smooth" });
    }
  }, [changeGroups]);

  const jumpTo = useCallback((idx: number) => {
    const clamped = Math.max(0, Math.min(idx, changeGroups.length - 1));
    setFocusIdx(clamped);
    scrollToGroup(clamped);
  }, [changeGroups, scrollToGroup]);

  const goPrev = useCallback(() => jumpTo(focusIdx - 1), [focusIdx, jumpTo]);
  const goNext = useCallback(() => jumpTo(focusIdx + 1), [focusIdx, jumpTo]);

  return (
    <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
      {/* Header row with labels + navigator */}
      <div className="flex shrink-0 border-b border-slate-200">
        <div className="flex-1 min-w-0 px-3 py-1.5 bg-indigo-50/60 border-r border-slate-200 flex items-center min-h-[32px]">
          <div className="size-1.5 rounded-full bg-indigo-400 mr-1.5" />
          <span className="text-[12px] font-semibold text-indigo-700">{beforeLabel}</span>
        </div>
        <div className="flex-1 min-w-0 px-3 py-1.5 bg-orange-50/60 flex items-center justify-between min-h-[32px]">
          <span className="flex items-center text-[12px] font-semibold text-orange-700">
            <div className="size-1.5 rounded-full bg-orange-400 mr-1.5" />
            {afterLabel}
          </span>
          {changeTotal > 0 && (
            <span className="flex items-center gap-0.5 ml-auto">
              <button onClick={goPrev} disabled={focusIdx <= 0}
                className="p-0.5 rounded text-slate-400 hover:text-indigo-600 disabled:text-slate-200 transition-colors" title="Previous change">
                <ChevronUp className="size-3.5" />
              </button>
              <span className="text-[11px] text-slate-400 tabular-nums min-w-[28px] text-center select-none">
                {focusIdx >= 0 ? focusIdx + 1 : "–"}/{changeTotal}
              </span>
              <button onClick={goNext} disabled={focusIdx >= changeTotal - 1}
                className="p-0.5 rounded text-slate-400 hover:text-indigo-600 disabled:text-slate-200 transition-colors" title="Next change">
                <ChevronDown className="size-3.5" />
              </button>
            </span>
          )}
        </div>
      </div>
      {/* Content panels — horizontal scrollable */}
      <div className="flex-1 flex min-h-0 overflow-hidden font-mono text-[12px] leading-[20px]">
        <div className="flex-1 min-w-0 flex flex-col border-r border-slate-200 min-h-0">
          <div ref={(el) => {
            beforePanelRef.current = el;
            if (typeof beforeRef === "function") beforeRef(el);
            else if (beforeRef && "current" in beforeRef) (beforeRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
          }} className="flex-1 overflow-auto scrollbar-mac">
            <div className="min-w-[70vw]">
              {beforeLines.map((l, i) => (
                <div key={i} className={`flex ${l.type === "removed" ? "bg-red-50/60" : "hover:bg-slate-50/50"}`}>
                  <span className="w-10 shrink-0 text-right pr-2 text-[10px] text-slate-300 select-none border-r border-slate-100 leading-[20px]">{l.num}</span>
                  <span className={`w-5 shrink-0 text-center select-none leading-[20px] ${l.type === "removed" ? "text-red-400 bg-red-100/40" : "text-transparent"}`}>{l.type === "removed" ? "−" : " "}</span>
                  <span className="flex-1 px-2 whitespace-pre-wrap break-all min-w-0">{l.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="flex-1 min-w-0 flex flex-col min-h-0">
          <div ref={(el) => {
            afterPanelRef.current = el;
            if (typeof afterRef === "function") afterRef(el);
            else if (afterRef && "current" in afterRef) (afterRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
          }} className="flex-1 overflow-auto scrollbar-mac">
            <div className="min-w-[70vw]">
              {afterLines.map((l, i) => (
                <div key={i} className={`flex ${l.type === "added" ? "bg-emerald-50/60" : "hover:bg-slate-50/50"}`}>
                  <span className="w-10 shrink-0 text-right pr-2 text-[10px] text-slate-300 select-none border-r border-slate-100 leading-[20px]">{l.num}</span>
                  <span className={`w-5 shrink-0 text-center select-none leading-[20px] ${l.type === "added" ? "text-emerald-500 bg-emerald-100/40" : "text-transparent"}`}>{l.type === "added" ? "+" : " "}</span>
                  <span className="flex-1 px-2 whitespace-pre-wrap break-all min-w-0">{l.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Count consecutive lines of a given type starting from idx */
function countType(lines: { type: string }[], idx: number, type: string): number {
  let c = 0;
  while (idx + c < lines.length && lines[idx + c].type === type) c++;
  return c;
}


function InlineAssetDiffPanels({
  beforeBody, afterBody, beforeLabel, afterLabel,
  changedAssets,
  noTextChanges, beforeHighlighted, afterHighlighted,
  changeActions, beforeTitle, afterTitle,
}: {
  beforeBody: string; afterBody: string;
  beforeLabel: string; afterLabel: string;
  changedAssets: { filename: string; action: string }[];
  slugPath?: string;
  noTextChanges: boolean;
  beforeHighlighted: string; afterHighlighted: string;
  changeActions: string[];
  beforeTitle?: string; afterTitle?: string;
}) {
  const { beforeRef, afterRef } = useSyncScroll(true);
  const beforeHRef = useRef<HTMLDivElement>(null);
  const afterHRef = useRef<HTMLDivElement>(null);

  // Horizontal scroll sync
  const hSyncing = useRef(false);
  useEffect(() => {
    const left = beforeHRef.current;
    const right = afterHRef.current;
    if (!left || !right) return;
    function onLeftScroll() {
      if (hSyncing.current) return;
      hSyncing.current = true;
      afterHRef.current!.scrollLeft = beforeHRef.current!.scrollLeft;
      hSyncing.current = false;
    }
    function onRightScroll() {
      if (hSyncing.current) return;
      hSyncing.current = true;
      beforeHRef.current!.scrollLeft = afterHRef.current!.scrollLeft;
      hSyncing.current = false;
    }
    left.addEventListener("scroll", onLeftScroll);
    right.addEventListener("scroll", onRightScroll);
    return () => {
      left.removeEventListener("scroll", onLeftScroll);
      right.removeEventListener("scroll", onRightScroll);
    };
  }, []);

  const { beforeAssetMap, afterAssetMap } = useMemo(() => {
    const bMap = new Map<string, string>();
    const aMap = new Map<string, string>();
    for (const a of changedAssets) {
      if (a.action === "removed") {
        bMap.set(a.filename, "removed");
      } else if (a.action === "added") {
        aMap.set(a.filename, "added");
      } else {
        bMap.set(a.filename, "modified");
        aMap.set(a.filename, "modified");
      }
    }
    return { beforeAssetMap: bMap, afterAssetMap: aMap };
  }, [changedAssets]);
  const titleChanged = beforeTitle && afterTitle && beforeTitle !== afterTitle;
  const isDeleted = changeActions.includes("deleted");
  const isAdded = changeActions.includes("added");

  return (
    <div className="flex-1 flex min-h-0 overflow-hidden">
      <div className="flex-1 min-w-0 flex flex-col border-r border-slate-200 min-h-0">
        <div className="px-3 py-1.5 bg-indigo-50/60 border-b border-slate-200 shrink-0 flex items-center">
          <div className="size-1.5 rounded-full bg-indigo-400 mr-1.5" />
          <span className="text-[12px] font-semibold text-indigo-700">{beforeLabel}</span>
          {titleChanged && <span className="ml-2 text-[11px] text-slate-400">{beforeTitle}</span>}
        </div>
        <div ref={(el) => {
          beforeHRef.current = el;
          if (typeof beforeRef === "function") beforeRef(el);
          else if (beforeRef && "current" in beforeRef) (beforeRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
        }} className="flex-1 overflow-auto p-4 scrollbar-mac">
          <div className="min-w-[70vw]">
            {isAdded ? <span className="text-slate-300 italic text-[13px]">New file</span>
             : !beforeBody ? <span className="text-slate-300 italic text-[13px]">Empty</span>
             : noTextChanges ? <MarkdownRenderer content={beforeBody} changedAssets={beforeAssetMap} />
             : isDeleted ? <div className="diff-segment-del"><MarkdownRenderer content={beforeBody} changedAssets={beforeAssetMap} /></div>
             : <MarkdownRenderer content={beforeHighlighted} changedAssets={beforeAssetMap} />}
          </div>
        </div>
      </div>
      <div className="flex-1 min-w-0 flex flex-col min-h-0">
        <div className="px-3 py-1.5 bg-orange-50/60 border-b border-slate-200 shrink-0 flex items-center">
          <div className="size-1.5 rounded-full bg-orange-400 mr-1.5" />
          <span className="text-[12px] font-semibold text-orange-700">{afterLabel}</span>
          {titleChanged && <span className="ml-2 text-[11px] text-slate-400">{afterTitle}</span>}
        </div>
        <div ref={(el) => {
          afterHRef.current = el;
          if (typeof afterRef === "function") afterRef(el);
          else if (afterRef && "current" in afterRef) (afterRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
        }} className="flex-1 overflow-auto p-4 scrollbar-mac">
          <div className="min-w-[70vw]">
            {isDeleted ? <span className="text-red-400 italic text-[13px]">Deleted</span>
             : !afterBody ? <span className="text-slate-300 italic text-[13px]">Empty</span>
             : noTextChanges ? <MarkdownRenderer content={afterBody} changedAssets={afterAssetMap} />
             : isAdded ? <div className="diff-segment-add"><MarkdownRenderer content={afterBody} changedAssets={afterAssetMap} /></div>
             : <MarkdownRenderer content={afterHighlighted} changedAssets={afterAssetMap} />}
          </div>
        </div>
      </div>
    </div>
  );
}
